import { Theme, css } from '@emotion/react';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import Badge from '@components/common/Badge';
import EFinderImage from '@components/common/EFinderImage';
import SvgIcon from '@components/common/SvgIcon';
import Pad from '@components/layout/Pad';

import IcPlay from '@public/icons/ic_play.svg';
import ProfileImg from '@public/icons/profile_32.svg';

import { IProfileByInfo } from '@base/outlookModel';
import { useGlobalContext } from '@contexts/global';
import { linearGradient, multilineEllipsis } from '@styles/css';
import { formatNumberMillion } from '@utils/chat';

const cardContainerCSS = (size: CardSizeObject) => (theme: Theme) =>
  css`
    display: flex;
    flex-direction: column;
    width: ${size.width}px;
    height: ${size.height}px;
    border-radius: ${Radius.small}px;
    box-shadow: 3px -2px 20px 3px #0000000d;
    overflow: hidden;

    .imageContainer {
      height: ${size.imageHeight}px;
      background-color: ${theme.var.white};
      position: relative;

      .gradient {
        position: absolute;
        width: 100%;
        height: 40px;
        top: 0;
        z-index: 1;
        ${linearGradient('top', 'rgba(255, 255, 255, 0)', 'rgba(0, 0, 0, 0.2)')}
      }

      .icon-btn {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 10;
        padding: 0;
        margin: 0;
      }

      .playCount {
        position: absolute;
        width: 100%;
        top: 0;
        display: flex;
        gap: ${Spacing.xxxsmall}px;
        padding: 10px;
        ${Typography.body11};
        color: ${theme.var.white};
        z-index: 10;

        svg path {
          fill: currentcolor;
        }
      }

      .image {
        width: 100%;
        height: 126px;
        background-color: ${theme.var.gray200};
      }
    }

    .info {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 10px 9px ${Spacing.medium}px;

      .name {
        ${Typography.body12};
        ${multilineEllipsis(1)}
      }
      .age {
        ${size.infoFontSize === 11 ? Typography.body11 : Typography.body12};
        margin-top: ${Spacing.xxsmall}px;
        margin-bottom: ${Spacing.xsmall}px;
        color: ${theme.var.gray500};
        ${multilineEllipsis(1)}
      }
      .desc {
        ${Typography.body12};
        font-weight: 600;
        ${multilineEllipsis(1)}
      }
    }
  `;

const badgeCSS = (isToday: boolean) => (theme: Theme) =>
  css`
    display: flex;
    align-items: center;
    position: absolute;
    right: 7px;
    top: 7px;
    font-weight: 600;
    font-color: ${theme.var.white};
    background: ${isToday
      ? `var(
          --promotion-gra,
          linear-gradient(57deg, #ff07f5 19.7%, #ed0064 80.3%)
        )`
      : theme.colors.honorGra};
    z-index: 11;
  `;

export type ProfileInfoGalleryCardSizeType = 'w166' | 'w128';

export interface CardSizeObject {
  width: number;
  height: number;
  imageHeight: number;
  infoFontSize: number;
}

export const ProfileInfoGalleryCardSize = {
  w166: {
    width: 166,
    height: 216,
    imageHeight: 140,
    infoFontSize: 12,
  },
  w128: {
    width: 128,
    height: 199,
    imageHeight: 126,
    infoFontSize: 11,
  },
};

interface ProfileInfoGalleryCardProps {
  data: IProfileByInfo;
  size: ProfileInfoGalleryCardSizeType;
  hanelOnClickCard?: (characterId: number) => void;
}

export type CharacterCardAction = 'modify' | 'delete' | 'detail';

const TODAY_FREE = '오늘만 무료!';

const ProfileInfoGalleryCard = ({
  data,
  size = 'w128',
  hanelOnClickCard,
}: ProfileInfoGalleryCardProps) => {
  const { isCheming } = useGlobalContext();
  const { id, image, title, information, payload, badge } = data;
  const defaultProfileImgSizeMapper = {
    w128: 128,
    w166: 166,
  };
  return (
    <div
      css={cardContainerCSS(ProfileInfoGalleryCardSize[size])}
      onClick={() => hanelOnClickCard && hanelOnClickCard(id)}
    >
      <div className="imageContainer">
        <Pad className="gradient" />
        <div>
          {!isCheming && (
            <div className="playCount">
              <SvgIcon icon={IcPlay} />
              <p>
                {formatNumberMillion(
                  payload.dynamic_object?.message_count || 0
                )}
              </p>
            </div>
          )}
        </div>
        {image && image.key ? (
          <>
            <EFinderImage image={image} alt={'profile_img'} />
            {badge && (
              <Badge text={badge} css={badgeCSS(badge === TODAY_FREE)} />
            )}
          </>
        ) : (
          <div className="image">
            <SvgIcon icon={ProfileImg} d={defaultProfileImgSizeMapper[size]} />
          </div>
        )}
      </div>
      <div className="info">
        <h6 className="name">{title}</h6>
        <p className="age">{information.sub_title}</p>
        <p className="desc">{information.description}</p>
      </div>
    </div>
  );
};

export default ProfileInfoGalleryCard;
